import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ChevronDown, X, ArrowUp, ArrowDown } from 'lucide-react';
import Pagination from 'react-responsive-pagination';
import 'react-responsive-pagination/themes/classic.css';
import AppLayout from '../components/AppLayout';
import Drawer from '../components/Drawer';
import CommonAreaFormSlider from '../components/CommonAreaFormSlider';
import CommonAreaDetailsSlider from '../components/CommonAreaDetailsSlider';
import { commonAreaService } from '../services/commonAreaService';
import { CommonArea } from '../types/commonArea';
import api from '../services/api';
import '../styles/Builders.css';
import '../styles/Buttons.css';
import '../styles/DataTable.css';
import '../styles/Contacts.css';

interface PaginationData {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

interface Project {
  _id: string;
  name: string;
}

interface CommonAreaFilters {
  projectId?: string;
  areaType?: string;
  status?: string;
  buildingType?: string;
}

const CommonAreas: React.FC = () => {
  const navigate = useNavigate();
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const [commonAreas, setCommonAreas] = useState<CommonArea[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<CommonAreaFilters>({});
  const [sortBy, setSortBy] = useState<string>('areaName');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [pagination, setPagination] = useState<PaginationData>({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });

  // Track previous values to determine what changed
  const prevFiltersRef = useRef<CommonAreaFilters>(filters);
  const prevSearchRef = useRef(searchQuery);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [areaToDelete, setAreaToDelete] = useState<CommonArea | null>(null);
  const [selectedCommonAreaId, setSelectedCommonAreaId] = useState<string | null>(null);

  // Get drawer state from URL params
  const isDrawerOpen = searchParams.get('action') === 'new' || !!searchParams.get('edit');
  const editAreaId = searchParams.get('edit');

  useEffect(() => {
    if (workspaceId) {
      fetchProjects();

      // Check if filters or search changed
      const filtersChanged = JSON.stringify(prevFiltersRef.current) !== JSON.stringify(filters);
      const searchChanged = prevSearchRef.current !== searchQuery;

      // Update refs
      prevFiltersRef.current = filters;
      prevSearchRef.current = searchQuery;

      // Initial load or search/filter change - full loading
      if (commonAreas.length === 0 || searchChanged || filtersChanged) {
        fetchCommonAreas(false);
      } else {
        // Data refresh (pagination only) - only tbody loading
        fetchCommonAreas(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId, pagination.page, pagination.limit, searchQuery, filters, sortBy, sortOrder]);

  const fetchProjects = async () => {
    if (!workspaceId) return;

    try {
      const response = await api.get(`/workspaces/${workspaceId}/projects`, {
        params: {
          limit: 1000,
          sortBy: 'name',
          sortOrder: 'asc'
        }
      });
      setProjects(response.data.projects);
    } catch (err: any) {
      console.error('Error fetching projects:', err);
    }
  };

  const fetchCommonAreas = async (isDataRefresh: boolean = false) => {
    if (!workspaceId) return;

    try {
      // Use dataLoading for data refreshes (pagination), loading for initial load
      if (isDataRefresh) {
        setDataLoading(true);
      } else {
        setLoading(true);
      }
      setError('');

      const params: any = {
        page: pagination.page,
        limit: pagination.limit,
        search: searchQuery,
        sortBy: sortBy,
        sortOrder: sortOrder
      };

      if (filters.projectId) params.projectId = filters.projectId;
      if (filters.areaType) params.areaType = filters.areaType;
      if (filters.status) params.status = filters.status;
      if (filters.buildingType) params.buildingType = filters.buildingType;

      const response = await commonAreaService.getAll(workspaceId, params);

      setCommonAreas(response.commonAreas);
      setPagination(response.pagination);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch common areas');
      console.error('Error fetching common areas:', err);
    } finally {
      if (isDataRefresh) {
        setDataLoading(false);
      } else {
        setLoading(false);
      }
    }
  };

  const handleBackToWorkspaces = () => {
    navigate('/workspaces');
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setPagination({ ...pagination, page: 1 });
  };

  const handleFilterChange = (key: keyof CommonAreaFilters, value: any) => {
    setFilters({ ...filters, [key]: value || undefined });
    setPagination({ ...pagination, page: 1 });
  };

  const handleClearFilters = () => {
    setFilters({});
    setSearchQuery('');
    setPagination({ ...pagination, page: 1 });
  };

  const handlePageChange = (page: number) => {
    setPagination({ ...pagination, page });
  };

  const handleLimitChange = (limit: number) => {
    setPagination({ ...pagination, limit, page: 1 }); // Reset to page 1 when changing limit
  };

  const handleSort = (column: string) => {
    if (sortBy === column) {
      // Toggle sort order if same column
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new column and default to ascending
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const getSortIcon = (column: string) => {
    if (sortBy !== column) {
      return null;
    }
    return sortOrder === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />;
  };

  const handleCreateArea = () => {
    setSearchParams({ action: 'new' });
  };

  const handleEditArea = (areaId: string) => {
    setSearchParams({ edit: areaId });
  };

  const handleRowClick = (areaId: string) => {
    setSelectedCommonAreaId(areaId);
  };

  const handleCloseDetailsSlider = () => {
    setSelectedCommonAreaId(null);
    fetchCommonAreas();
  };

  const handleCloseDrawer = () => {
    setSearchParams({});
    fetchCommonAreas();
  };

  const handleDeleteClick = (area: CommonArea) => {
    setAreaToDelete(area);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!areaToDelete || !workspaceId) return;

    try {
      await commonAreaService.delete(workspaceId, areaToDelete._id);
      setShowDeleteModal(false);
      setAreaToDelete(null);
      fetchCommonAreas();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete common area');
      console.error('Error deleting common area:', err);
    }
  };

  const getStatusBadgeClass = (status?: string) => {
    switch (status) {
      case 'ACTIVE':
        return 'badge-available';
      case 'UNDER_CONSTRUCTION':
        return 'badge-reserved';
      case 'PLANNED':
        return 'badge-unavailable';
      case 'INACTIVE':
        return 'badge-sold';
      default:
        return '';
    }
  };

  if (!workspaceId) {
    return (
      <AppLayout onBack={handleBackToWorkspaces} backLabel="Workspaces">
        <div className="builders-container">
          <div className="builders-error">
            <p>No workspace selected</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout onBack={handleBackToWorkspaces} backLabel="Workspaces">
      <>
        <div className="builders-container">
          {/* Section Header */}
          <div className="builders-section-header">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h2 className="builders-section-title">Common Areas</h2>
                <p className="builders-section-subtitle">
                  Manage common areas and amenities
                </p>
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button className="btn btn-outline btn-md" onClick={handleClearFilters}>
                  Clear
                </button>
                <button className="btn btn-primary btn-md" onClick={handleCreateArea}>
                  + Add New Common Area
                </button>
              </div>
            </div>
          </div>

          {/* Card Container for Filters and Table */}
          <div style={{
            backgroundColor: '#FFFFFF',
            borderRadius: '12px',
            padding: '24px',
            boxShadow: '0 2px 8px rgba(72, 24, 47, 0.08)'
          }}>
            {/* Filters */}
            <div className="builders-actions" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', marginBottom: '20px' }}>
            <select
              className="form-input"
              value={filters.projectId || ''}
              onChange={(e) => handleFilterChange('projectId', e.target.value)}
            >
              <option value="">Select Project</option>
              {projects.map((project) => (
                <option key={project._id} value={project._id}>
                  {project.name}
                </option>
              ))}
            </select>

            <select
              className="form-input"
              value={filters.areaType || ''}
              onChange={(e) => handleFilterChange('areaType', e.target.value)}
            >
              <option value="">Select Area Type</option>
              <option value="AMENITY">Amenity</option>
              <option value="UTILITY">Utility</option>
              <option value="CIRCULATION">Circulation</option>
              <option value="PARKING">Parking</option>
              <option value="STORAGE">Storage</option>
              <option value="OTHER">Other</option>
            </select>

            <select
              className="form-input"
              value={filters.status || ''}
              onChange={(e) => handleFilterChange('status', e.target.value)}
            >
              <option value="">Select Status</option>
              <option value="ACTIVE">Active</option>
              <option value="UNDER_CONSTRUCTION">Under Construction</option>
              <option value="PLANNED">Planned</option>
              <option value="INACTIVE">Inactive</option>
            </select>

            <select
              className="form-input"
              value={filters.buildingType || ''}
              onChange={(e) => handleFilterChange('buildingType', e.target.value)}
            >
              <option value="">Select Building Type</option>
              <option value="Condominium">Condominium</option>
              <option value="Townhouse">Townhouse</option>
              <option value="Detached">Detached</option>
              <option value="Semi-Detached">Semi-Detached</option>
            </select>
          </div>

          {/* Search */}
          <div className="builders-actions" style={{ marginBottom: '20px' }}>
            <div style={{ position: 'relative', flex: 1 }}>
              <input
                type="text"
                className="form-input"
                placeholder="Search"
                value={searchQuery}
                onChange={handleSearchChange}
                style={{ paddingRight: searchQuery ? '36px' : '12px', width: '100%' }}
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  style={{
                    position: 'absolute',
                    right: '8px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#6B7280',
                    transition: 'color 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.color = '#374151'}
                  onMouseLeave={(e) => e.currentTarget.style.color = '#6B7280'}
                  aria-label="Clear search"
                >
                  <X size={16} />
                </button>
              )}
            </div>
          </div>

            {/* Error Display */}
            {error && (
              <div className="builders-error" style={{ marginBottom: '20px' }}>
                <p>{error}</p>
              </div>
            )}

            {/* Loading State */}
            {loading ? (
              <div className="builders-container">
                <div className="builders-loading">
                  <div className="builders-loading-spinner" />
                </div>
              </div>
            ) : (
              <>
            {/* Common Areas Table - Full Width */}
            <div style={{ overflowX: 'auto', width: '100%' }}>
              <div className="data-table-container" style={{ minWidth: '1400px', position: 'relative' }}>
                {dataLoading && (
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 10
                  }}>
                    <div className="contacts-loading-spinner"></div>
                  </div>
                )}
                <table className="data-table">
                <thead>
                  <tr>
                    <th>
                      <button
                        onClick={() => handleSort('projectName')}
                        className="sort-button"
                        disabled={dataLoading}
                      >
                        Project
                        <span className="sort-icon">
                          {getSortIcon('projectName')}
                        </span>
                      </button>
                    </th>
                    <th>
                      <button
                        onClick={() => handleSort('level')}
                        className="sort-button"
                        disabled={dataLoading}
                      >
                        Level
                        <span className="sort-icon">
                          {getSortIcon('level')}
                        </span>
                      </button>
                    </th>
                    <th>
                      <button
                        onClick={() => handleSort('areaName')}
                        className="sort-button"
                        disabled={dataLoading}
                      >
                        Name
                        <span className="sort-icon">
                          {getSortIcon('areaName')}
                        </span>
                      </button>
                    </th>
                    <th>
                      <button
                        onClick={() => handleSort('area')}
                        className="sort-button"
                        disabled={dataLoading}
                      >
                        Area (Sq.Ft)
                        <span className="sort-icon">
                          {getSortIcon('area')}
                        </span>
                      </button>
                    </th>
                    <th>
                      <button
                        onClick={() => handleSort('buildingType')}
                        className="sort-button"
                        disabled={dataLoading}
                      >
                        Building Type
                        <span className="sort-icon">
                          {getSortIcon('buildingType')}
                        </span>
                      </button>
                    </th>
                    <th>
                      <button
                        onClick={() => handleSort('areaType')}
                        className="sort-button"
                        disabled={dataLoading}
                      >
                        Area Type
                        <span className="sort-icon">
                          {getSortIcon('areaType')}
                        </span>
                      </button>
                    </th>
                    <th>
                      <button
                        onClick={() => handleSort('status')}
                        className="sort-button"
                        disabled={dataLoading}
                      >
                        Status
                        <span className="sort-icon">
                          {getSortIcon('status')}
                        </span>
                      </button>
                    </th>
                    <th>
                      <button
                        onClick={() => handleSort('capacity')}
                        className="sort-button"
                        disabled={dataLoading}
                      >
                        Capacity
                        <span className="sort-icon">
                          {getSortIcon('capacity')}
                        </span>
                      </button>
                    </th>
                    <th>Operating Hours</th>
                    <th>Accessible</th>
                    <th>Key Access</th>
                  </tr>
                </thead>
                <tbody style={{ opacity: dataLoading ? 0.5 : 1, transition: 'opacity 0.2s', cursor: dataLoading ? 'wait' : 'default' }}>
                  {commonAreas.length === 0 ? (
                    <tr>
                      <td colSpan={11} className="data-table-empty">
                        No common areas found. Click "+ Add New Common Area" to create one.
                      </td>
                    </tr>
                  ) : (
                    commonAreas.map((area) => (
                      <tr
                        key={area._id}
                        onClick={() => handleRowClick(area._id)}
                      >
                        <td className="data-table-primary-cell">{area.projectName || '-'}</td>
                        <td>{area.level || '-'}</td>
                        <td className="data-table-primary-cell">{area.areaName}</td>
                        <td>{area.area || '-'}</td>
                        <td>{area.buildingType || '-'}</td>
                        <td>{area.areaType || '-'}</td>
                        <td>
                          <span className={`data-table-status ${getStatusBadgeClass(area.status)}`}>
                            {area.status}
                          </span>
                        </td>
                        <td>{area.capacity || '-'}</td>
                        <td>{area.operatingHours || '-'}</td>
                        <td>{area.isAccessible ? 'Yes' : 'No'}</td>
                        <td>{area.requiresKeyAccess ? 'Yes' : 'No'}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
              </div>
            </div>

            {/* Pagination and Records Per Page */}
            <div className="contacts-pagination-controls">
              {/* Records Per Page Selector */}
              <div className="contacts-records-per-page">
                <span>Show</span>
                <select
                  value={pagination.limit}
                  onChange={(e) => handleLimitChange(Number(e.target.value))}
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <span>records per page (Total: {pagination.total})</span>
              </div>

              {/* Pagination */}
              {pagination.pages > 1 && (
                <div className="contacts-pagination-wrapper">
                  <Pagination
                    current={pagination.page}
                    total={pagination.pages}
                    onPageChange={handlePageChange}
                    maxWidth={400}
                  />
                </div>
              )}
            </div>
              </>
            )}
          </div>
        </div>

        {/* Drawer for Create/Edit */}
        <Drawer
          isOpen={isDrawerOpen}
          onClose={handleCloseDrawer}
          title={editAreaId ? 'Edit Common Area' : 'Add Common Area'}
          footer={
            <div className="drawer-footer-actions">
              <button type="button" className="btn btn-secondary btn-md" onClick={handleCloseDrawer} form="common-area-form">
                Cancel
              </button>
              <button type="submit" className="btn btn-primary btn-md" form="common-area-form">
                {editAreaId ? 'Update' : 'Add'}
              </button>
            </div>
          }
        >
          {isDrawerOpen && (
            <CommonAreaFormSlider
              workspaceId={workspaceId}
              commonAreaId={editAreaId || undefined}
              onSuccess={handleCloseDrawer}
              onCancel={handleCloseDrawer}
            />
          )}
        </Drawer>

        {/* Delete Confirmation Modal */}
        {showDeleteModal && (
          <div className="builders-modal-overlay" onClick={() => setShowDeleteModal(false)}>
            <div className="builders-modal" onClick={(e) => e.stopPropagation()}>
              <h3>Delete Common Area</h3>
              <p>Are you sure you want to delete "{areaToDelete?.areaName}"?</p>
              <p style={{ fontSize: '13px', color: '#6B7280', marginTop: '10px' }}>
                This action cannot be undone.
              </p>
              <div className="builders-modal-actions">
                <button className="btn btn-secondary btn-md" onClick={() => setShowDeleteModal(false)}>
                  Cancel
                </button>
                <button className="btn btn-danger btn-md" onClick={handleDeleteConfirm}>
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Common Area Details Slider */}
        {selectedCommonAreaId && (
          <CommonAreaDetailsSlider
            workspaceId={workspaceId}
            commonAreaId={selectedCommonAreaId}
            onClose={handleCloseDetailsSlider}
          />
        )}
      </>
    </AppLayout>
  );
};

export default CommonAreas;
